import { SocialLikes } from '@/models/Social/social-likes';
import request from '@/Utilities/httpRequest';

export enum SocialThreadType{
    Question,
    Answer,
    Comment,
    PendingComment
}

interface SocialThreadAuthor{
    name: string,
    date: string,
    verified: boolean
}

interface SocialDialogProduct{
    name: string,
    image: string,
    groupId?: string
}

interface SocialDialogKStrings{
    reply?: string,
    more?: string, 
    less?: string, 
    translations?: string,
    originals?: string,
    answered?: string,
    asked?: string,
    commented?: string,
    pendingReview?: string,
    newReplyText?: string
}


export class SocialThread {
    protected _id: string;
    protected _author?: SocialThreadAuthor;
    protected _text: string;
    protected _translation?: string;
    protected _type?: SocialThreadType;
    protected _reply?: Array<SocialThread>;
    protected _hasReply: boolean = true;

    public constructor(id: string, text: string){
        this._id = id;
        this._text = text;
        //this._type = SocialThreadType.Comment;
    }

    /****************** GET *****************/
    public get id(): string {
        return this._id;
    }

    public get text(): string {
        return this._text;
    }

    public get translation(): string|undefined {
        return this._translation;
    }

    public get type(): SocialThreadType{
        if(this._type){
            return this._type;
        }
        if(this.reply && this.reply.length>0){
            return SocialThreadType.Question;
        }
        if(this.author?.verified){
            return SocialThreadType.Answer;
        }
        return this._type ?? SocialThreadType.Comment;
    }

    public get author(): SocialThreadAuthor|undefined {
        return this._author;
    }

    public get reply(): Array<SocialThread>{
        return this._reply ?? [];
    }

    public get hasReply(): boolean{
        return this._hasReply;
    }


    /****************** SET *****************/

    public set id(value: string){
        this._id = value;
    }

    public set text(value: string){
        this._text = value;
    }

    public set translation(value: string|undefined){
        this._translation = value;
    }

    public set type(value: SocialThreadType){
        this._type = value;
    }

    public set reply(value: Array<SocialThread>){
        this._reply = value;
    }

    public set author(value: SocialThreadAuthor|undefined){
        this._author = value;
    }

    public set hasReply(value: boolean){
        this._hasReply = value;
    }

    /****************** FUNCTIONS *****************/

    /**
     * Add new SocialThread to the reply list
     * @param value 
     */
    public addReply(value: SocialThread){
        if(this._reply === undefined){
            this._reply = new Array<SocialThread>();
        }
        this._reply.push(value);
    }

    /**
     * Test if exists translations in the texts
     * @returns boolean
     */
    public hasTranslations(): boolean{
        if(this._translation){
            return true;
        }
        if(this._reply){
            for(var i = 0; i < this._reply.length; i++){
                if(this._reply[i].hasTranslations()){
                    return true;
                }                
            }
        }
        return false;
    }
}

export class SocialDialog {
    protected _thread: SocialThread;
    protected _product?: SocialDialogProduct;
    protected _likes?: SocialLikes;
    protected _kStrings?: SocialDialogKStrings;
    protected _hasTranslations: boolean;
    protected _loading: boolean = false;
    protected _allowReply: boolean = false;
    protected _moreUrl?: string;
    protected _moreParams?: object;
    protected _postId: string;
    protected _replyLoaded: boolean = false;

    public constructor(thread: SocialThread, postId: string){
        this._thread = thread;
        this._hasTranslations = thread.hasTranslations();
        this._postId = postId;
    }

    public get product(): SocialDialogProduct|undefined{ return this._product;}
    public set product(value: SocialDialogProduct|undefined){this._product = value;}

    public get likes(): SocialLikes|undefined{return this._likes;}
    public set likes(value: SocialLikes|undefined){this._likes = value;}

    public get thread(): SocialThread{return this._thread;}
    public set thread(value: SocialThread){this._thread = value;}

    public get kStrings(): SocialDialogKStrings{
        return this._kStrings ?? {
            reply: 'Reply', 
            more: 'More', 
            less: 'Less', 
            translations: 'Translations', 
            originals: 'Originals', 
            answered: 'Answered', 
            asked: 'Asked',
            commented: 'Commented',
            pendingReview: 'Pending review',
            newReplyText: 'Add a comment reply!',
        };
    }
    public set kStrings(value: SocialDialogKStrings){this._kStrings = value;}

    public get hasTranslations(): boolean{return this._hasTranslations;}
    public set hasTranslations(value: boolean){this._hasTranslations = value;}

    public get allowReply(): boolean{return this._allowReply;}
    public set allowReply(value: boolean){this._allowReply = value;}

    public get loading(): boolean{return this._loading;}
    public set loading(value: boolean){this._loading = value;}

    public get postId(): string{return this._postId;}

    public setMore(url: string, params: object){
        this._moreUrl = url;
        this._moreParams = params;
    }

    public async getReplies(){
        if(this._replyLoaded || !(this.thread?.hasReply)){
            return false;
        } 
        this.loading = true;
        let params: any = {...this._moreParams,...{postId: this._postId, parent: this.thread.id}};
        let { data } = await request.post(this._moreUrl,params,false);
        let elements = data?.elements ?? [];
        elements.forEach((e:any) => {
            let r:SocialThread|undefined = this._createReply(e);
            if(r){
                this.thread.addReply(r);
                this.thread.type = SocialThreadType.Question;
            }
        });
        this.loading = false;
        this._replyLoaded = true;
    }

    protected _createReply(e:any): SocialThread|undefined{
        let id     = e?.id ?? null;
        let text   = e?.text ?? null;
        if(id && text){
            let thread = new SocialThread(id, text);
            thread.author = e?.author ?? undefined;
            thread.type   = e?.type ?? undefined;
            thread.translation = e?.translation ?? undefined;
            let reply:Array<any> = e?.reply ?? [];
            reply.forEach((rpl: any) => {
                let r:SocialThread|undefined = this._createReply(rpl);
                if(r){
                    thread.addReply(r);
                    thread.type = SocialThreadType.Question;
                }
            });
            return thread;
        }
        return undefined;
    }
}